import React, { useEffect, useState } from 'react';
import { Loader, Stack, TextInput, Text, Checkbox } from '@mantine/core';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { useDebouncedValue } from '@mantine/hooks';
import type { QuizListFragment } from '../../graphql/graphql';
import { useQuizService } from '../../services/QuizService';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import styled from '@emotion/styled';

export type SearchQuizProps = {
	onSelected: (quiz: QuizListFragment | undefined) => void;
};

export default function SearchQuiz(props: SearchQuizProps) {
	const { onSelected } = props;

	const quizService = useQuizService();

	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const [quizzes, setQuizzes] = useState<QuizListFragment[]>([]);
	const [selectedQuiz, setSelectedQuiz] = useState<QuizListFragment | undefined>(undefined);
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 500);

	useEffect(() => {
		if (debouncedSearchQuery) {
			const loader = addLoader();
			const request = quizService.searchByTitle(debouncedSearchQuery);
			setSelectedQuiz(undefined);
			request.response
				.then((quizzes) => {
					setQuizzes(quizzes);
				})
				.catch((err) => {
					NotificationUtils.showError(err as Error, 'Failed to search quizzes');
				})
				.finally(() => {
					removeLoader(loader);
				});
		}
	}, [addLoader, quizService, debouncedSearchQuery, removeLoader]);

	const handleQuizSelected = (event: React.ChangeEvent<HTMLInputElement>, quiz: QuizListFragment) => {
		setSelectedQuiz(event.currentTarget.checked ? quiz : undefined);
		onSelected(event.currentTarget.checked ? quiz : undefined);
	};

	return (
		<Stack>
			<TextInput
				value={searchQuery}
				onChange={(event) => {
					// A new search clears the ticked result, so the parent must not keep the old one.
					setSearchQuery(event.currentTarget.value);
					setSelectedQuiz(undefined);
					onSelected(undefined);
				}}
				label="Search quizzes by title"
				disabled={isLoading}
			/>
			{isLoading && (
				<Text ta="center">
					<Loader size="xs" ml="xs" />
				</Text>
			)}
			{!isLoading &&
				debouncedSearchQuery &&
				(quizzes.length ? (
					<Grid>
						{quizzes.map((a) => (
							<div key={a.id}>
								<div>{a.title}</div>
								<div>
									<Checkbox
										checked={selectedQuiz?.id === a.id}
										onChange={(e) => handleQuizSelected(e, a)}
									/>
								</div>
							</div>
						))}
					</Grid>
				) : (
					<Text ta="center">No search results</Text>
				))}
		</Stack>
	);
}

const Grid = styled.div`
	max-height: 500px;
	overflow-y: auto;
	> div {
		display: grid;
		width: 100%;
		grid-template-columns: 1fr auto;
		padding: 0.5rem;
		gap: 0.5rem;
		align-items: center;
		overflow-wrap: anywhere;

		&:nth-child(even) {
			background: var(--mantine-color-gray-2);
		}
	}
`;
